import Button from "components/form/Button";
import { signProps } from "types/paymentMethods";
import CustomModal from "components/ui-component/CustomModal";
import { SignIcon } from "icons/payments/SignatureIcon";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  formatCurrency,
  formatNumber,
  getGeneralDetailMarkers,
  replaceMarkers,
} from "utils/HelperFunctions";
import SignatureComponent from "./SignatureComponent";
import ESignConsent from "./ESignConsent";
import { Dialog } from "@headlessui/react";
import { GreenTrueIcon } from "icons/payments/GreenTrueIcon";
import moment from "moment";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { useAppSelector } from "store/hooks";
import Skeleton from "react-loading-skeleton";
import { SettingsValue } from "types/payments";
import { useUserData } from "store/slices/authSlice";
import { RootState } from "store/store";
import { useTid, useTestAttrsFactory } from "utils/testid";
import { formatDisplayDate } from "utils/DateFormat";
import { useStateDisclaimer } from "hooks/useStateDisclaimer";
import { isEsignConsentEnabledForCluster } from "utils/esignConsent";

interface SignAgreementProps {
  currentStep: number;
  tempData?: any;
  cardData?: any;
  orderData?: any;
  handleBack?: any;
  handleSaveSign?: any;
  savedSignature?: string;
  savedConsentAt?: string;
  handleConsent?: (consentAt: string) => void;
  isStax?: boolean;
}
const STEP_TITLE_CLASS =
  "mt-1 lg:text-2xl text-xl font-medium leading-normal text-iconObscure w-[90%] break-words";

// Both wizard steps stay mounted for the slide; only the visible one is the
// dialog's title, so screen readers announce the step the customer is on.
const StepTitle = ({
  active,
  children,
}: {
  active: boolean;
  children: React.ReactNode;
}) =>
  active ? (
    <Dialog.Title as="h3" className={STEP_TITLE_CLASS}>
      {children}
    </Dialog.Title>
  ) : (
    <h3 className={STEP_TITLE_CLASS}>{children}</h3>
  );

interface SignListProps {
  lable: string;
  id: string;
}

const SignAgreement = ({
  currentStep,
  tempData,
  orderData,
  handleBack = () => {},
  handleSaveSign,
  savedSignature = "",
  savedConsentAt = "",
  handleConsent = () => {},
  isStax = false,
}: SignAgreementProps) => {
  const userData = useUserData();
  const tid = useTid();
  const testAttrs = useTestAttrsFactory();
  const [isLoading] = useState<boolean>(false);
  const [isOpenSign, setIsOpenSign] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<any>("Create Signature");
  const portalSettings = usePortalSettingsData();
  const { paymentMarkers, companyMarkers, companyDetails, conFee } =
    useAppSelector((state: RootState) => state.payment);
  // Clusters with E-SIGN consent get a two-step wizard: consent (step 1),
  // then the signature (step 2). The rest keep the single signature step.
  const usesConsentStep = isEsignConsentEnabledForCluster(orderData?.clusterid);
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  const [isConsentChecked, setIsConsentChecked] = useState<boolean>(false);
  // The page mounts a desktop and a mobile copy of this step, so consent is
  // kept by the parent (and the store) rather than in either copy.
  const consentAt = savedConsentAt;
  // Set when consent is asked from "Next" (a signature restored from an
  // earlier visit with no consent): accepting it carries straight on.
  const [continueAfterConsent, setContinueAfterConsent] =
    useState<boolean>(false);

  const initialSign: signProps = {
    signTitle: "",
    file: null,
    fileUrl: "",
    signDraw: null,
  };
  const [signData, setSignData] = useState<signProps>(initialSign);
  const [finalSign, setFinalSign] = useState<any>({
    fileURL: "",
    signTitle: "",
  });
  const [isDrawing, setIsDrawing] = useState<boolean>(false);

  useEffect(() => {
    if (!savedSignature) return;

    setFinalSign({
      fileURL: savedSignature,
      signTitle: "",
    });
  }, [savedSignature]);

  const signList: SignListProps[] = [
    { lable: "Create Signature", id: "option1" },
    { lable: "Draw Signature", id: "option2" },
    { lable: "Upload Signature", id: "option3" },
  ];

  const handleRadioChange = (value: any) => {
    setSelectedValue(value);
  };

  const sigCanvas = useRef<any>({});
  const fileInputRef = useRef<any>();

  /* a function that uses the canvas ref to clear the canvas 
  via a method given by react-signature-canvas */
  const clear = () => {
    sigCanvas.current.clear();
    setIsDrawing(false);
  };

  const handleBeginDrawing = () => {
    setIsDrawing(true);
  };
  const onFileInputChanges = (e: any) => {
    // Handle file input changes here
    setSignData((prevData) => ({
      ...prevData,
      fileUrl: URL.createObjectURL(e.target.files[0]),
      file: e.target.files[0],
    }));
  };

  const handleDivClick = () => {
    if (signData?.file) {
      setSignData({ ...signData, file: null, fileUrl: "" });
      setTimeout(() => {
        fileInputRef.current.click();
      }, 0);
    } else {
      fileInputRef.current.click();
    }
  };

  const handleSave = () => {
    if (selectedValue === "Draw Signature") {
      setFinalSign((prev: any) => ({
        signTitle: "",
        fileURL: sigCanvas.current.getTrimmedCanvas().toDataURL("image/png"),
      }));
    } else if (selectedValue === "Upload Signature") {
      setFinalSign((prev: any) => ({
        signTitle: "",
        fileURL: signData.fileUrl,
      }));
    } else {
      setFinalSign((prev: any) => ({
        fileURL: "",
        signTitle: signData.signTitle,
      }));
    }
    setIsOpenSign(false);
  };

  const onCloseModal = () => {
    setIsOpenSign(false);
    setIsDrawing(false);
    setSignData(initialSign);
    setSelectedValue("Create Signature");
    setIsConsentChecked(false);
    setContinueAfterConsent(false);
    // Consent, once given, is kept: closing the wizard (Cancel, ✕, backdrop,
    // Esc) after looking at or changing the signature must not ask for it
    // again on Next.
  };

  // Once consent is given, reopening the wizard (e.g. to change a saved
  // signature) goes straight to the signature step.
  const openSignatureWizard = () => {
    if (usesConsentStep) setWizardStep(consentAt ? 2 : 1);
    setIsOpenSign(true);
  };

  const handleConsentContinue = () => {
    if (!isConsentChecked) return;
    const acceptedAt = new Date().toISOString();
    handleConsent(acceptedAt);
    if (continueAfterConsent) {
      setContinueAfterConsent(false);
      setIsConsentChecked(false);
      setIsOpenSign(false);
      handleSaveSign({ ...finalSign, consentAt: acceptedAt });
      return;
    }
    setWizardStep(2);
  };

  // A signature restored from an earlier visit may have no consent with it:
  // Next then asks for consent first and continues once it is accepted.
  const handleNext = () => {
    if (usesConsentStep && !consentAt) {
      setContinueAfterConsent(true);
      setIsConsentChecked(false);
      setWizardStep(1);
      setIsOpenSign(true);
      return;
    }
    handleSaveSign(usesConsentStep ? { ...finalSign, consentAt } : finalSign);
  };

  // Company contacts come from the company details the page footer loads (the
  // same ones it shows), falling back to the company markers.
  const consentContacts = useMemo(() => {
    const phone = companyDetails?.phone_number
      ? formatNumber(
          companyDetails.phone_number,
          companyDetails?.countryCode || "1",
          true
        ) || ""
      : "";
    return {
      companyName:
        paymentMarkers?.["Company Legal Name"] ||
        companyDetails?.company_name ||
        companyDetails?.name ||
        companyMarkers?.["Company Legal Name"] ||
        "",
      customerEmail: userData?.data?.accountData?.email || "",
      supportPhone: phone || companyMarkers?.["Support Phone"] || "",
      supportEmail:
        companyDetails?.email || companyMarkers?.["Support Email"] || "",
    };
  }, [
    companyDetails,
    companyMarkers,
    paymentMarkers,
    userData?.data?.accountData?.email,
  ]);

  const hasPortalSettings = Object.keys(portalSettings || {}).length > 0;
  const usesAuthorizationOverride =
    portalSettings?.authorization_override === SettingsValue.YES;
  const usesTermsOverride = portalSettings?.tnc_override === SettingsValue.YES;
  const usesStateDisclaimer = usesAuthorizationOverride || usesTermsOverride;
  const stateDisclaimer = useStateDisclaimer(
    currentStep === 2 && hasPortalSettings && usesStateDisclaimer
  );
  const isTextLoading =
    !hasPortalSettings ||
    (currentStep === 2 &&
      usesStateDisclaimer &&
      (stateDisclaimer.status === "idle" ||
        stateDisclaimer.status === "loading"));
  const paymentAmount = tempData?.[0]?.amount;
  const billingStartDate = orderData?.billing_start_date;
  const numberOfPayments = tempData?.length ?? 0;
  const totalAmount = useMemo(
    () =>
      (tempData ?? []).reduce(
        (sum: number, item: any) => sum + Number(item?.amount || 0),
        0
      ),
    [tempData]
  );

  const { authText, tncText } = useMemo(() => {
    try {
      let generalMarkers = getGeneralDetailMarkers({
        conFee: conFee,
        paymentAmount,
        paymentAgreement: window.location.href,
        nextDate: billingStartDate,
      });
      // Agreement-level markers the portal settings text can reference;
      // these come from the schedule being signed, not from the backend.
      const agreementMarkers = {
        "General: Number of Payments": String(numberOfPayments),
        "General: Total Amount": formatCurrency(totalAmount) || "$0.00",
        "General: Start Date": formatDisplayDate(
          billingStartDate,
          "MM/DD/YYYY"
        ),
      };
      const canUseStateText = stateDisclaimer.status === "succeeded";
      const authorizationSource =
        usesAuthorizationOverride && canUseStateText
          ? stateDisclaimer.data?.authorization_text
          : portalSettings?.pp_authorization_value;
      const termsSource =
        usesTermsOverride && canUseStateText
          ? stateDisclaimer.data?.tnc_text
          : portalSettings?.pp_term_condition_value;
      const auth = replaceMarkers(authorizationSource ?? "", {
        ...paymentMarkers,
        ...generalMarkers,
        ...agreementMarkers,
      });
      const tnc = replaceMarkers(termsSource ?? "", {
        ...paymentMarkers,
        ...generalMarkers,
        ...agreementMarkers,
      });

      return { authText: auth || "", tncText: tnc || "" };
    } catch (error) {
      console.log("handleUpdateContent error =====", error);
      return { authText: "", tncText: "" };
    }
  }, [
    billingStartDate,
    conFee,
    numberOfPayments,
    paymentAmount,
    paymentMarkers,
    portalSettings?.pp_authorization_value,
    portalSettings?.pp_term_condition_value,
    stateDisclaimer.data?.authorization_text,
    stateDisclaimer.data?.tnc_text,
    stateDisclaimer.status,
    totalAmount,
    usesAuthorizationOverride,
    usesTermsOverride,
  ]);

  const isCustom = useMemo(() => {
    if (tempData?.length > 0) {
      return tempData?.[0]?.schedule_type?.toLowerCase() === "custom";
    }

    return false;
  }, [tempData]);

  // The signature options and Cancel/Save buttons, shared by the old
  // single-step wizard and step 2 of the consent wizard.
  const signatureFields = (
    <>
      <div className="flex md:flex-row flex-col lg:justify-between lg:gap-0 gap-3 pt-5 lg:text-xl text-lg text-txtPrimary leading-[30px]">
        {signList?.map((items: SignListProps, index: number) => {
          return (
            <div
              key={items.id}
              data-testid={tid(`signature-option-${items.id}`)}
            >
              <div className="flex gap-2">
                <input
                  className="customRadio focus-visible:outline-none cursor-pointer"
                  type="radio"
                  id={items.id}
                  aria-label={items.lable}
                  data-testid={tid(`signature-option-input-${items.id}`)}
                  value={selectedValue}
                  checked={selectedValue === items.lable}
                  onChange={() => {
                    handleRadioChange(items.lable);
                    setIsDrawing(false);
                  }}
                />
                <label
                  htmlFor={items.id}
                  className={`text-base  ${
                    selectedValue === items.lable
                      ? "text-txtPrimary"
                      : "text-txtSecondary"
                  }`}
                >
                  {items.lable}
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <SignatureComponent
        selectedValue={selectedValue}
        signData={signData}
        setSignData={setSignData}
        handleBeginDrawing={handleBeginDrawing}
        handleDivClick={handleDivClick}
        onFileInputChanges={onFileInputChanges}
        sigCanvas={sigCanvas}
        isDrawing={isDrawing}
        fileInputRef={fileInputRef}
        clear={clear}
      />

      <div
        className={` w-full
      pt-5 flex lg:justify-end gap-5`}
      >
        <Button
          text="Cancel"
          variant="stroke-outline"
          onClick={onCloseModal}
          className="lg:w-fit w-full"
          isDisabled={isLoading}
          {...testAttrs("signature-modal-cancel", "Cancel signature")}
        />
        <Button
          text="Save"
          variant="variable"
          isDisabled={
            signData?.signTitle?.length > 0 || isDrawing || signData?.file
              ? false
              : true
          }
          onClick={handleSave}
          isLoading={isLoading}
          className="lg:w-fit w-full"
          {...testAttrs("signature-modal-save", "Save signature")}
        />
      </div>
    </>
  );

  const consentStep = (
    <div>
      <p className="text-sm leading-[21px] text-txtSecondary">Step 1 of 2</p>
      <StepTitle active={wizardStep === 1}>
        Consent to Electronic Records and Signatures
      </StepTitle>
      <ESignConsent
        {...consentContacts}
        checked={isConsentChecked}
        setChecked={setIsConsentChecked}
        onCancel={onCloseModal}
        onContinue={handleConsentContinue}
      />
    </div>
  );

  return (
    <div
      className="lg:border-t lg:border-strokePrimary lg:pt-8 lg:text-xl text-lg font-medium"
      {...testAttrs("sign-agreement-step", "Sign agreement step")}
    >
      <p
        className={`${
          currentStep === 2 ? "text-btnPrimaryActive" : "text-txtPrimary"
        } relative lg:flex lg:h-10 lg:items-center`}
      >
        Sign agreement
      </p>
      <div
        className={`w-full ${window.innerWidth > 769 && "signature"} ${
          currentStep === 2 ? "open !h-[calc(var(--shell-vh) - 280px)]" : ""
        } `}
      >
        <div
          className={`SubTextCommon leading-[30px] text-LightTextParagraph mt-2 lg:pr-4`}
        >
          <div className="mt-6 flex flex-col gap-6">
            <div>
              <p className="lg:text-xl text-lg font-medium text-txtPrimary">
                Payment Information
              </p>
              <p className="lg:text-xl text-lg font-normal text-txtSecondary mt-3">
                You will be making {tempData.length}
                {tempData.length === 1 ? (
                  " payment "
                ) : (
                  <>
                    {" "}
                    {orderData?.payment_schedule !== "custom" && (
                      <>
                        <span className="">
                          {orderData?.payment_schedule}
                        </span>{" "}
                      </>
                    )}
                    payments{" "}
                  </>
                )}
                {isCustom ? (
                  <></>
                ) : (
                  <>
                    in the amount of{" "}
                    {formatCurrency(tempData[0]?.amount) || "$0.00"}{" "}
                  </>
                )}
                totaling {formatCurrency(totalAmount) || "$0.00"} starting on{" "}
                {formatDisplayDate(orderData?.billing_start_date, "MM/DD/YYYY")}
                .
              </p>
              {(orderData.cc_fee_amount > 0 ||
                orderData.ach_fee_amount > 0) && (
                <p className="lg:text-xl text-lg font-normal text-txtSecondary mt-3">
                  {isStax
                    ? "Surcharge"
                    : portalSettings?.cc_fee_label
                      ? portalSettings?.cc_fee_label
                      : "Convenience Fee"}{" "}
                  of
                  {orderData.cc_fee_amount > 0
                    ? formatCurrency(orderData.cc_fee_amount) || "$0.00"
                    : formatCurrency(orderData.ach_fee_amount) || "$0.00"}{" "}
                  is added to each installment payment.
                </p>
              )}
            </div>
            <div className="h-[1px] bg-strokeActive w-full" />
            <div>
              <p className="lg:text-xl text-lg font-medium text-txtPrimary">
                Terms and Conditions
              </p>
              {isTextLoading ? (
                [1, 2, 3]?.map((_, index) => (
                  <div className="flex flex-col gap-1" key={index}>
                    <Skeleton className="w-full h-[22px]" />
                  </div>
                ))
              ) : (
                <p
                  id="payment-agreement-tnc"
                  className="lg:text-xl text-lg font-normal text-txtSecondary mt-3"
                  data-testid={tid("terms-and-conditions")}
                  dangerouslySetInnerHTML={{ __html: tncText }}
                ></p>
              )}
            </div>
            <div className="h-[1px] bg-strokeActive w-full" />
            <div>
              <p className="lg:text-xl text-lg font-medium text-txtPrimary">
                Authorization
              </p>
              {isTextLoading ? (
                [1, 2, 3]?.map((_, index) => (
                  <div className="flex flex-col gap-1" key={index}>
                    <Skeleton className="w-full h-[22px]" />
                  </div>
                ))
              ) : (
                <p
                  id="payment-agreement-auth"
                  className="lg:text-xl text-lg font-normal text-txtSecondary mt-3"
                  data-testid={tid("authorization")}
                  dangerouslySetInnerHTML={{ __html: authText }}
                ></p>
              )}
            </div>

            <div className="h-[1px] bg-strokeActive w-full" />
            <div>
              <p className="lg:text-xl text-lg font-medium text-txtPrimary">
                Cardholder Information
              </p>
              <div className="flex mt-6 ">
                <div className="w-1/2 flex flex-col gap-1">
                  <p className="text-lg leading-[23.44px] text-txtPrimary font-medium">
                    Cardholder Name
                  </p>
                  <p className="text-base text-txtSecondary font-normal leading-[20.83px]">
                    {userData?.data?.accountData?.first_name || ""}{" "}
                    {userData?.data?.accountData?.last_name || ""}{" "}
                  </p>
                </div>
                <div className="w-1/2 flex flex-col gap-1">
                  <p className="text-lg leading-[23.44px] text-txtPrimary font-medium">
                    Date
                  </p>
                  <p className="text-base text-txtSecondary font-normal leading-[20.83px]">
                    {moment().format("MM/DD/YYYY")}{" "}
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:text-xl text-lg font-medium text-txtPrimary">
              <p>Cardholder Signature</p>
              <div
                className="mt-[5px] cursor-pointer rounded-md border-strokePrimary border-[1px] w-full flex justify-center outline-none focus-visible:border-btnPrimaryActive focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-btnPrimaryActive"
                role="button"
                tabIndex={0}
                {...testAttrs("open-signature-wizard", "Open signature wizard")}
                onClick={openSignatureWizard}
              >
                {finalSign?.fileURL ? (
                  <>
                    <div className="w-[400px] h-[80px] py-3 px-3">
                      <img
                        src={finalSign.fileURL}
                        alt="Cardholder signature"
                        className="w-full h-[100%] object-contain"
                      />
                    </div>
                  </>
                ) : finalSign?.signTitle ? (
                  <div className="flex justify-center w-full">
                    <p className="signInputClass text-[44px] p-3">
                      {finalSign.signTitle}
                    </p>
                  </div>
                ) : (
                  <div className="py-3 flex justify-center gap-2">
                    {" "}
                    <SignIcon aria-hidden="true" />
                    <p>Signature</p>
                  </div>
                )}
              </div>
            </div>
            {currentStep === 2 && (
              <>
                <div className="w-full flex justify-between items-center lg:gap-0 gap-5">
                  <Button
                    text="Back"
                    variant="white"
                    className="lg:!w-40 !w-full"
                    {...testAttrs(
                      "signature-back",
                      "Back to agreement details"
                    )}
                    onClick={() => {
                      handleBack();
                    }}
                  />
                  <Button
                    text="Next"
                    type="button"
                    className="lg:!w-40 !w-full"
                    onClick={handleNext}
                    variant="variable"
                    {...testAttrs(
                      "signature-next",
                      "Continue to payment method"
                    )}
                    isDisabled={
                      finalSign?.signTitle === "" && finalSign?.fileURL === ""
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <CustomModal
        isLoading={isLoading}
        isOpen={isOpenSign}
        title={usesConsentStep ? undefined : "Signature Wizard"}
        aria-label={
          usesConsentStep
            ? wizardStep === 1
              ? "Consent to Electronic Records and Signatures"
              : "Signature Wizard"
            : undefined
        }
        doneText="Save"
        className={
          usesConsentStep
            ? "w-[600px] max-w-full rounded-[10px] lg:!px-10 px-5 lg:!pb-7"
            : "w-[600px] md:!h-[400px] !h-[435px] rounded-[10px] lg:!px-10 px-5"
        }
        cancleText="Cancel"
        signModal={true}
        onDoneClick={() => {
          // handleCancelPaymentPlan();
        }}
        onCloseClick={() => {
          onCloseModal();
        }}
        children={
          usesConsentStep ? (
            // Both steps stay mounted and slide past each other like a
            // carousel; the inactive one is taken out of the flow so the
            // modal fits the active step's height.
            <div className="relative overflow-hidden -mx-1 px-1">
              <div
                aria-hidden={wizardStep !== 1}
                className={`transition-[transform,visibility] duration-300 ease-in-out motion-reduce:transition-none ${
                  wizardStep === 1
                    ? "relative w-full translate-x-0 visible"
                    : "absolute inset-x-1 top-0 -translate-x-[calc(100%+8px)] invisible"
                }`}
                {...testAttrs("esign-consent-step", "E-SIGN consent")}
              >
                {consentStep}
              </div>
              <div
                aria-hidden={wizardStep !== 2}
                className={`transition-[transform,visibility] duration-300 ease-in-out motion-reduce:transition-none ${
                  wizardStep === 2
                    ? "relative w-full translate-x-0 visible"
                    : "absolute inset-x-1 top-0 translate-x-[calc(100%+8px)] invisible"
                }`}
                {...testAttrs("esign-signature-step", "Signature step")}
              >
                <p className="text-sm leading-[21px] text-txtSecondary">
                  Step 2 of 2
                </p>
                <StepTitle active={wizardStep === 2}>
                  Signature Wizard
                </StepTitle>
                {consentAt && (
                  <p
                    className="mt-2 flex items-center gap-1.5 text-sm leading-[21px] text-TextGreen"
                    {...testAttrs("esign-consent-recorded")}
                  >
                    <GreenTrueIcon
                      aria-hidden="true"
                      strokeColor="currentColor"
                    />
                    Consent recorded at{" "}
                    {moment(consentAt).format("h:mm A")}
                  </p>
                )}
                {signatureFields}
              </div>
            </div>
          ) : (
            <Fragment>{signatureFields}</Fragment>
          )
        }
      />
    </div>
  );
};

export default SignAgreement;
